import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, RefreshCw, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

const TaskTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    task_type: '',
    default_priority: 'medium',
    recurring_frequency: 'monthly',
    applicable_client_types: ['regular'],
    due_day: 11,
    advance_days: 5,
    auto_assign_to_role: 'none'
  });
  const [formLoading, setFormLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/task-templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
      alert('Failed to fetch task templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      const templateData = {
        ...formData,
        due_day: parseInt(formData.due_day),
        advance_days: parseInt(formData.advance_days),
        auto_assign_to_role: formData.auto_assign_to_role === 'none' ? null : formData.auto_assign_to_role
      };
      
      if (editingTemplate) {
        await axios.put(`${API}/task-templates/${editingTemplate.id}`, templateData);
      } else {
        await axios.post(`${API}/task-templates`, templateData);
      }
      await fetchTemplates();
      resetForm();
      setShowModal(false);
    } catch (error) {
      console.error('Error saving template:', error);
      alert(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setFormLoading(false);
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      task_type: template.task_type,
      default_priority: template.default_priority,
      recurring_frequency: template.recurring_frequency,
      applicable_client_types: template.applicable_client_types,
      due_day: template.due_day,
      advance_days: template.advance_days,
      auto_assign_to_role: template.auto_assign_to_role || 'none'
    });
    setShowModal(true);
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      await axios.delete(`${API}/task-templates/${templateId}`);
      await fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Failed to delete template');
    }
  };

  const handleGenerateTasks = async () => {
    if (!window.confirm('This will auto-generate tasks for all applicable clients based on active templates. Continue?')) return;

    try {
      setGenerating(true);
      const response = await axios.post(`${API}/task-templates/generate-tasks`);
      alert(response.data.message);
      await fetchTemplates();
    } catch (error) {
      console.error('Error generating tasks:', error);
      alert('Failed to generate tasks');
    } finally {
      setGenerating(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      task_type: '',
      default_priority: 'medium',
      recurring_frequency: 'monthly',
      applicable_client_types: ['regular'],
      due_day: 11,
      advance_days: 5,
      auto_assign_to_role: 'none'
    });
    setEditingTemplate(null);
  };

  const getFrequencyLabel = (freq) => {
    const labels = {
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      annual: 'Annual',
      one_time: 'One Time'
    };
    return labels[freq] || freq;
  };

  const getClientTypeLabels = (types) => {
    const labels = {
      regular: 'Regular GST',
      composition: 'Composition',
      regular_annual: 'Regular + Annual'
    };
    return types.map(t => labels[t] || t).join(', ');
  };

  const handleClientTypeToggle = (type) => {
    const current = formData.applicable_client_types;
    if (current.includes(type)) {
      setFormData({...formData, applicable_client_types: current.filter(t => t !== type)});
    } else {
      setFormData({...formData, applicable_client_types: [...current, type]});
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
            Task Templates
          </h2>
          <p className="text-gray-600 mt-2">Create templates for recurring tasks that auto-generate for clients</p>
        </div>
        <div className="flex gap-3">
          <Button 
            onClick={handleGenerateTasks}
            disabled={generating}
            className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 shadow-lg hover:shadow-xl transition-all duration-200"
          >
            <RefreshCw className={`w-5 h-5 mr-2 ${generating ? 'animate-spin' : ''}`} />
            {generating ? 'Generating...' : 'Generate Tasks'}
          </Button>
          <Button 
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 shadow-lg hover:shadow-xl transition-all duration-200"
          >
            <Plus className="w-5 h-5 mr-2" />
            Create Template
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white border-0 shadow-lg">
          <CardContent className="p-6 text-center">
            <p className="text-sm font-semibold uppercase tracking-wide mb-2 opacity-90">Total Templates</p>
            <div className="text-3xl font-black">{templates.length}</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white border-0 shadow-lg">
          <CardContent className="p-6 text-center">
            <p className="text-sm font-semibold uppercase tracking-wide mb-2 opacity-90">Active</p>
            <div className="text-3xl font-black">{templates.filter(t => t.is_active).length}</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-500 to-pink-600 text-white border-0 shadow-lg">
          <CardContent className="p-6 text-center">
            <p className="text-sm font-semibold uppercase tracking-wide mb-2 opacity-90">Monthly</p>
            <div className="text-3xl font-black">{templates.filter(t => t.recurring_frequency === 'monthly').length}</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-500 to-red-600 text-white border-0 shadow-lg">
          <CardContent className="p-6 text-center">
            <p className="text-sm font-semibold uppercase tracking-wide mb-2 opacity-90">Quarterly/Annual</p>
            <div className="text-3xl font-black">{templates.filter(t => t.recurring_frequency === 'quarterly' || t.recurring_frequency === 'annual').length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Template List */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="shadow-lg">
              <CardContent className="p-6 space-y-4">
                <div className="h-6 bg-gray-200 rounded w-3/4 animate-pulse"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse"></div>
                <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : templates.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 px-4 text-center">
          <div className="w-32 h-32 rounded-3xl bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center mx-auto mb-8">
            <FileText className="w-16 h-16 text-blue-600" />
          </div>
          <h3 className="text-3xl font-bold text-gray-900 mb-4">No Templates Yet</h3>
          <p className="text-lg text-gray-600 max-w-md mb-8 leading-relaxed">
            Create task templates to automatically generate recurring tasks for your clients.
          </p>
          <Button 
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold px-10 py-6 text-lg rounded-2xl shadow-lg hover:shadow-xl transition-all duration-200 h-14"
          >
            <Plus className="w-6 h-6 mr-2" />
            Create First Template
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template) => (
            <Card key={template.id} className="bg-white shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 rounded-2xl border border-gray-200">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{template.name}</h3>
                    <p className="text-sm text-gray-600 mb-1">
                      <strong>Type:</strong> {template.task_type}
                    </p>
                    <p className="text-sm text-gray-600">
                      <strong>Frequency:</strong> {getFrequencyLabel(template.recurring_frequency)}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${template.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                    {template.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>

                {template.description && (
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{template.description}</p>
                )}

                <div className="mb-4 space-y-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase">Applies To</p>
                  <p className="text-sm text-gray-700">{getClientTypeLabels(template.applicable_client_types)}</p>
                  
                  <p className="text-xs text-gray-600 mt-2">
                    <strong>Due Day:</strong> Day {template.due_day} | <strong>Advance:</strong> {template.advance_days} days
                  </p>
                  
                  {template.auto_assign_to_role && (
                    <p className="text-xs text-blue-600">
                      <strong>Auto-assign to:</strong> {template.auto_assign_to_role}
                    </p>
                  )}
                </div>

                <div className="flex gap-2 mt-4">
                  <Button
                    onClick={() => handleEdit(template)}
                    variant="outline"
                    className="flex-1 border-2 border-blue-200 text-blue-600 hover:bg-blue-50"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                  <Button
                    onClick={() => handleDelete(template.id)}
                    variant="outline"
                    className="border-2 border-red-200 text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={(open) => {
        setShowModal(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              {editingTemplate ? 'Edit Template' : 'Create Task Template'}
            </DialogTitle>
            <DialogDescription>
              {editingTemplate ? 'Update task template details' : 'Create a template for recurring tasks'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label htmlFor="name" className="text-sm font-semibold">Template Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  placeholder="e.g., GSTR-1 Filing"
                  className="mt-2"
                />
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="description" className="text-sm font-semibold">Description</Label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Additional details..."
                  className="mt-2 w-full p-2 border rounded-md min-h-[60px]"
                />
              </div>

              <div>
                <Label htmlFor="task_type" className="text-sm font-semibold">Task Type *</Label>
                <Input
                  id="task_type"
                  value={formData.task_type}
                  onChange={(e) => setFormData({...formData, task_type: e.target.value})}
                  required
                  placeholder="e.g., GST Filing"
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="default_priority" className="text-sm font-semibold">Default Priority *</Label>
                <Select value={formData.default_priority} onValueChange={(value) => setFormData({...formData, default_priority: value})}>
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="recurring_frequency" className="text-sm font-semibold">Frequency *</Label>
                <Select value={formData.recurring_frequency} onValueChange={(value) => setFormData({...formData, recurring_frequency: value})}>
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="annual">Annual</SelectItem>
                    <SelectItem value="one_time">One Time</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="due_day" className="text-sm font-semibold">Due Day *</Label>
                <Input
                  id="due_day"
                  type="number"
                  min="1"
                  max={formData.recurring_frequency === 'annual' ? '365' : '31'}
                  value={formData.due_day}
                  onChange={(e) => setFormData({...formData, due_day: e.target.value})}
                  required
                  className="mt-2"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.recurring_frequency === 'monthly' && 'Day of month (1-31)'}
                  {formData.recurring_frequency === 'quarterly' && 'Day of quarter-end month'}
                  {formData.recurring_frequency === 'annual' && 'Day of year (1-365)'}
                </p>
              </div>

              <div>
                <Label htmlFor="advance_days" className="text-sm font-semibold">Create Advance (days) *</Label>
                <Input
                  id="advance_days"
                  type="number"
                  min="0"
                  value={formData.advance_days}
                  onChange={(e) => setFormData({...formData, advance_days: e.target.value})}
                  required
                  className="mt-2"
                />
                <p className="text-xs text-gray-500 mt-1">Create task X days before due date</p>
              </div>

              <div>
                <Label htmlFor="auto_assign_to_role" className="text-sm font-semibold">Auto-assign to Role</Label>
                <Select value={formData.auto_assign_to_role || 'none'} onValueChange={(value) => setFormData({...formData, auto_assign_to_role: value === 'none' ? '' : value})}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="No auto-assignment" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No auto-assignment</SelectItem>
                    <SelectItem value="assistant">Assistant</SelectItem>
                    <SelectItem value="junior_ca">Junior CA</SelectItem>
                    <SelectItem value="senior_ca">Senior CA</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="md:col-span-2">
                <Label className="text-sm font-semibold mb-2 block">Applicable Client Types *</Label>
                <div className="flex flex-wrap gap-3 mt-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.applicable_client_types.includes('regular')}
                      onChange={() => handleClientTypeToggle('regular')}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Regular GST</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.applicable_client_types.includes('composition')}
                      onChange={() => handleClientTypeToggle('composition')}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Composition</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.applicable_client_types.includes('regular_annual')}
                      onChange={() => handleClientTypeToggle('regular_annual')}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Regular + Annual</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="flex-1"
                disabled={formLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
                disabled={formLoading}
              >
                {formLoading ? 'Saving...' : (editingTemplate ? 'Update Template' : 'Create Template')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TaskTemplates;
